import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Inbox } from 'lucide-react';

// Shared mobile "Manage" UI primitives — dark theme, volt accent (#ffd108).

export const ManageHeader = ({ title, subtitle, backTo = '/mobile/manage', right }) => {
  const navigate = useNavigate();
  return (
    <div className="bg-[var(--m-bg-card)] px-3 py-3 sticky top-0 z-20 safe-top flex items-center gap-2">
      <button
        onClick={() => navigate(backTo)}
        className="w-9 h-9 rounded-lg flex items-center justify-center text-[var(--m-text-body)] hover:bg-[var(--m-bg-elevated)] shrink-0"
        data-testid="manage-back-btn"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <div className="flex-1 min-w-0">
        <h1 className="text-[var(--m-text)] font-bold text-lg truncate" data-testid="manage-screen-title">{title}</h1>
        {subtitle && <p className="text-[var(--m-text-secondary)] text-xs truncate">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
};

export const SearchBar = ({ value, onChange, placeholder = 'Search...' }) => (
  <div className="relative">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--m-text-muted)]" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg pl-9 pr-3 py-2.5 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)] focus:outline-none focus:border-[#ffd108]"
      data-testid="manage-search-input"
    />
  </div>
);

export const Fab = ({ onClick, label = 'Add', testId = 'manage-fab', visible = true }) => {
  if (!visible) return null;
  return (
    <button
      onClick={onClick}
      className="fixed right-4 bottom-[calc(88px+env(safe-area-inset-bottom,0px))] z-40 flex items-center gap-2 bg-[#ffd108] text-[#1a1a1a] font-semibold text-sm pl-4 pr-5 py-3 rounded-full shadow-lg active:scale-95 transition-transform"
      data-testid={testId}
    >
      <Plus className="h-5 w-5" /> {label}
    </button>
  );
};

export const EmptyState = ({ title = 'Nothing here yet', subtitle }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid="manage-empty">
    <div className="w-14 h-14 rounded-2xl bg-[var(--m-bg-card)] flex items-center justify-center mb-3">
      <Inbox className="h-7 w-7 text-[var(--m-text-muted)]" />
    </div>
    <p className="text-[var(--m-text)] font-medium">{title}</p>
    {subtitle && <p className="text-[var(--m-text-muted)] text-sm mt-1">{subtitle}</p>}
  </div>
);

export const Field = ({ label, required, hint, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-[var(--m-text-body)] flex items-center gap-1">
      {label} {required && <span className="text-[#ffd108]">*</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-[var(--m-text-muted)]">{hint}</p>}
  </div>
);

const inputCls =
  'w-full bg-[var(--m-bg-input)] border border-[var(--m-border)] rounded-lg px-3 py-2.5 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)] focus:outline-none focus:border-[#ffd108]';

export const TextInput = ({ testId, ...props }) => (
  <input className={inputCls} data-testid={testId} {...props} />
);

export const TextArea = ({ testId, ...props }) => (
  <textarea rows={3} className={inputCls} data-testid={testId} {...props} />
);

export const SelectInput = ({ testId, options = [], placeholder = 'Select...', ...props }) => (
  <select className={inputCls} data-testid={testId} {...props}>
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export const Toggle = ({ label, checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="w-full flex items-center justify-between bg-[var(--m-bg-input)] border border-[var(--m-border)] rounded-lg px-3 py-2.5"
    data-testid={testId}
  >
    <span className="text-sm text-[var(--m-text-body)]">{label}</span>
    <span className={`w-10 h-6 rounded-full transition-colors relative ${checked ? 'bg-[#ffd108]' : 'bg-[var(--m-border-light)]'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </span>
  </button>
);
